import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus } from 'react-icons/fi';

const emptyForm = { email: '', password: '', firstName: '', lastName: '', roleCode: 'support_staff' };

const PlatformStaff = () => {
  const { success, error: showError } = useToast();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadAll = () => {
    setLoading(true);
    apiClient.get('/platform/staff')
      .then(({ data }) => setStaff(data.data))
      .catch((err) => showError(getErrorMessage(err, 'Could not load platform staff')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/platform/staff', form);
      success('✅ Platform staff account created');
      setShowForm(false);
      setForm(emptyForm);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not create staff account')); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Platform Staff</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0' }}>Accounts with platform-wide access, not scoped to any tenant</p>
        </div>
        <button className="btn btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
          <FiPlus size={16} /> {showForm ? 'Cancel' : 'New Staff Account'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="form-grid" style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div className="form-field"><label>First Name *</label><input required value={form.firstName} onChange={(e) => setForm(p => ({ ...p, firstName: e.target.value }))} /></div>
          <div className="form-field"><label>Last Name *</label><input required value={form.lastName} onChange={(e) => setForm(p => ({ ...p, lastName: e.target.value }))} /></div>
          <div className="form-field"><label>Email *</label><input required type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} /></div>
          <div className="form-field"><label>Password *</label><input required type="password" minLength={8} value={form.password} onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} /></div>
          <div className="form-field">
            <label>Role *</label>
            <select value={form.roleCode} onChange={(e) => setForm(p => ({ ...p, roleCode: e.target.value }))}>
              <option value="support_staff">Support Staff</option>
              <option value="super_admin">Super Admin (full access)</option>
            </select>
          </div>
          <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Create Account</button></div>
        </form>
      )}

      <div className="table-container" style={{ background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
        <table className="data-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role(s)</th><th>Status</th><th>Created</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
            ) : staff.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No platform staff yet</td></tr>
            ) : staff.map((s) => (
              <tr key={s.id}>
                <td style={{ fontWeight: 600 }}>{s.firstName} {s.lastName}</td>
                <td>{s.email}</td>
                <td>
                  {s.roleCodes.length === 0 ? (
                    <span style={{ color: '#9ca3af' }}>No role assigned</span>
                  ) : s.roleCodes.map((code) => (
                    <span key={code} className="badge" style={{ background: code === 'super_admin' ? '#7c3aed' : '#3b82f6', color: 'white', marginRight: '0.3rem' }}>
                      {code.replace('_', ' ')}
                    </span>
                  ))}
                </td>
                <td><span className="badge" style={{ background: s.status === 'active' ? '#10b981' : '#9ca3af', color: 'white' }}>{s.status}</span></td>
                <td>{new Date(s.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

export default PlatformStaff;